import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftIcon,
  Cog6ToothIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { SaveBar } from "../../../components/utils/SaveBar";
import { UnsavedChangesDialog } from "../../../components/utils/Prompt";
import ModernEmployeeSelector from "../../../components/utils/Selector/ModernEmployeeSelector";
import { tasksApi } from "../../../api/tasks";
import TodoTemplateEditDialog from "./TodoTemplateEditDialog";
import {
  buildTemplateSummaryParts,
  compareTemplates,
  emptyTemplate,
  normalizeTemplatesForCompare,
} from "./todoTemplateSettingsUtils";

export const TodoSettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [spawningId, setSpawningId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [employeePositions, setEmployeePositions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [descriptionOpenRows, setDescriptionOpenRows] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [employeePickerIndex, setEmployeePickerIndex] = useState(null);
  const [selectedEmployeesByRow, setSelectedEmployeesByRow] = useState({});
  const [confirmEditClose, setConfirmEditClose] = useState(false);

  const applyLoadedData = useCallback((data) => {
    const rows = Array.isArray(data.templates) ? data.templates : [];
    const nextTemplates = rows.length ? rows : [emptyTemplate()];
    setTemplates(nextTemplates);
    setEmployeePositions(Array.isArray(data.employee_positions) ? data.employee_positions : []);
    setCategories(Array.isArray(data.categories) ? data.categories : []);
    setSavedSnapshot(normalizeTemplatesForCompare(nextTemplates));
  }, []);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tasksApi.getSettings();
      applyLoadedData(response?.data?.data || {});
    } catch {
      toast.error(t("todo.settingsLoadError"));
      const fallback = [emptyTemplate()];
      setTemplates(fallback);
      setSavedSnapshot(normalizeTemplatesForCompare(fallback));
    } finally {
      setLoading(false);
    }
  }, [applyLoadedData, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const hasChanges = useMemo(() => {
    if (savedSnapshot == null) return false;
    return (
      JSON.stringify(normalizeTemplatesForCompare(templates)) !== JSON.stringify(savedSnapshot)
    );
  }, [templates, savedSnapshot]);

  const activeEmployeePositions = useMemo(
    () => employeePositions.filter((row) => row.active !== false),
    [employeePositions]
  );

  const sortedTemplateRows = useMemo(() => {
    return templates
      .map((row, index) => ({ row, index }))
      .sort((a, b) => compareTemplates(a.row, b.row, employeePositions));
  }, [templates, employeePositions]);

  const updateTemplate = (index, patch) => {
    setSaveSuccess(false);
    setSaveError(false);
    setTemplates((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addTemplate = () => {
    setTemplates((prev) => {
      const next = [...prev, emptyTemplate()];
      setEditingIndex(next.length - 1);
      return next;
    });
  };

  const removeTemplate = (index) => {
    setTemplates((prev) => {
      const next = prev.length <= 1 ? [emptyTemplate()] : prev.filter((_, i) => i !== index);
      return next;
    });
    if (editingIndex === index) setEditingIndex(null);
  };

  const applyCategoryToTemplate = (index, category) => {
    if (!category) return;
    const positionIds = employeePositions
      .filter((row) => row.category === category && row.id)
      .map((row) => row.id);
    updateTemplate(index, {
      category,
      target_employee_position_ids: [
        ...new Set([...(templates[index]?.target_employee_position_ids || []), ...positionIds]),
      ],
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(false);
    try {
      const response = await tasksApi.updateSettings({
        templates: templates
          .filter((row) => row.title?.trim())
          .map((row) => ({
            ...row,
            title: row.title.trim(),
            category: row.category?.trim() || "",
            target_employee_position_ids: row.target_employee_position_ids || [],
            target_user_ids: row.target_user_ids || [],
            task_reference_type: row.task_reference_id ? "Hse::Document" : "",
          })),
      });

      applyLoadedData(response?.data?.data || {});
      setSaveSuccess(true);
      toast.success(t("todo.settingsSaved"));
      return true;
    } catch {
      setSaveError(true);
      toast.error(t("todo.settingsSaveError"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSpawn = async (templateId, event) => {
    event?.stopPropagation();
    if (!templateId) return;
    setSpawningId(templateId);
    try {
      const response = await tasksApi.spawnTemplate(templateId);
      if (response?.data?.success) {
        toast.success(t("todo.settingsSpawnSuccess"));
        if (response?.data?.data?.templates) {
          applyLoadedData({
            templates: response.data.data.templates,
            employee_positions: employeePositions,
            categories,
          });
        }
      } else {
        toast.error(response?.data?.errors?.[0] || t("todo.settingsSpawnError"));
      }
    } catch (err) {
      toast.error(err?.response?.data?.errors?.[0] || t("todo.settingsSpawnError"));
    } finally {
      setSpawningId(null);
    }
  };

  const selectedEmployeesForRow = useMemo(() => {
    if (employeePickerIndex == null) return [];
    const row = templates[employeePickerIndex];
    const ids = row?.target_user_ids || [];
    return ids.map((id) => selectedEmployeesByRow[id] || { id, name: `#${id}` });
  }, [employeePickerIndex, templates, selectedEmployeesByRow]);

  const editingTemplate = editingIndex != null ? templates[editingIndex] : null;

  const closeEditDialog = useCallback(() => {
    setEditingIndex(null);
    setConfirmEditClose(false);
  }, []);

  const requestCloseEditDialog = useCallback(() => {
    if (employeePickerOpen) return;
    if (hasChanges) {
      setConfirmEditClose(true);
      return;
    }
    closeEditDialog();
  }, [employeePickerOpen, hasChanges, closeEditDialog]);

  useEffect(() => {
    if (editingIndex == null || employeePickerOpen || confirmEditClose) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") requestCloseEditDialog();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editingIndex, employeePickerOpen, confirmEditClose, requestCloseEditDialog]);

  const handleSaveAndCloseEdit = async () => {
    const saved = await handleSave();
    if (saved) closeEditDialog();
  };

  const handleSaveFromBar = async () => {
    const saved = await handleSave();
    if (saved && editingIndex != null) closeEditDialog();
  };

  if (loading) {
    return (
      <div className="project-plan-sitelane td-app">
        <DashboardHeader title={t("todo.settingsTitle")} />
        <ResourceLoading />
      </div>
    );
  }

  return (
    <div className="project-plan-sitelane td-app min-h-0 flex flex-1 flex-col overflow-hidden">
      <DashboardHeader title={t("todo.settingsTitle")} />
      <div className="flex-1 overflow-auto bg-[#F6F8FB] p-4 pb-28 sm:p-6">
        <div className="mx-auto max-w-4xl space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link
              to="/dashboard/todos"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              {t("todo.backToTasks")}
            </Link>
            <Link
              to="/dashboard/company/settings?tab=employee_positions"
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <Cog6ToothIcon className="h-4 w-4" />
              {t("todo.settingsManagePositions")}
            </Link>
          </div>

          <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t("todo.settingsTemplatesTitle")}</h2>
              <p className="mt-1 text-sm text-gray-500">{t("todo.settingsTemplatesHelpCompact")}</p>
              <p className="mt-1 text-xs text-gray-400">{t("todo.settingsListHint")}</p>
            </div>

            <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {sortedTemplateRows.map(({ row, index }) => {
                const summaryParts = buildTemplateSummaryParts(row, {
                  t,
                  employeePositions: activeEmployeePositions,
                  selectedEmployeesByRow,
                });

                return (
                  <div
                    key={row.id || `template-${index}`}
                    className={`flex items-start gap-3 p-3 hover:bg-gray-50/80 ${
                      row.active === false ? "bg-gray-50/60 opacity-80" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => setEditingIndex(index)}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${
                            row.active !== false ? "bg-emerald-500" : "bg-gray-300"
                          }`}
                          aria-hidden
                        />
                        <span className="truncate text-sm font-medium text-gray-900">
                          {row.title?.trim() || t("todo.settingsUntitled")}
                        </span>
                        {row.active === false ? (
                          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                            {t("todo.settingsTemplateInactive")}
                          </span>
                        ) : null}
                      </div>
                      {row.active === false ? (
                        <p className="mt-1 pl-4 text-xs text-gray-400">{t("todo.settingsTemplateInactiveHint")}</p>
                      ) : null}
                      {summaryParts.length ? (
                        <p className="mt-1 truncate pl-4 text-xs text-gray-500">{summaryParts.join(" · ")}</p>
                      ) : (
                        <p className="mt-1 pl-4 text-xs text-gray-400">{t("todo.settingsNoSummary")}</p>
                      )}
                    </button>

                    <div className="flex shrink-0 items-center gap-1">
                      {row.id ? (
                        <button
                          type="button"
                          className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={spawningId === row.id || row.active === false}
                          title={row.active === false ? t("todo.settingsTemplateInactiveHint") : undefined}
                          onClick={(event) => handleSpawn(row.id, event)}
                        >
                          {spawningId === row.id ? t("todo.settingsSpawning") : t("todo.settingsSpawnNow")}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        title={t("todo.settingsEditTemplate")}
                        onClick={() => setEditingIndex(index)}
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                        title={t("todo.settingsRemoveTemplate")}
                        onClick={() => removeTemplate(index)}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <datalist id="todo-template-category-options">
              {categories.map((category) => (
                <option key={category} value={category} />
              ))}
            </datalist>

            <button
              type="button"
              className="mt-4 inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              onClick={addTemplate}
            >
              <PlusIcon className="h-4 w-4" />
              {t("todo.settingsAddTemplate")}
            </button>
          </section>
        </div>
      </div>

      <TodoTemplateEditDialog
        open={editingIndex != null}
        onClose={requestCloseEditDialog}
        blockClose={employeePickerOpen}
        template={editingTemplate}
        templateIndex={editingIndex}
        categories={categories}
        activeEmployeePositions={activeEmployeePositions}
        selectedEmployeesByRow={selectedEmployeesByRow}
        expanded={Boolean(expandedRows[editingIndex])}
        descriptionOpen={
          Boolean(descriptionOpenRows[editingIndex]) || Boolean(editingTemplate?.description?.trim())
        }
        onToggleExpanded={(index) =>
          setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }))
        }
        onToggleDescription={(index, open) =>
          setDescriptionOpenRows((prev) => ({ ...prev, [index]: open }))
        }
        onUpdate={updateTemplate}
        onOpenAssignees={(index) => {
          setEmployeePickerIndex(index);
          setEmployeePickerOpen(true);
        }}
        onApplyCategory={applyCategoryToTemplate}
      />

      <ModernEmployeeSelector
        open={employeePickerOpen}
        useDialog
        multiple
        closeOnSelect={false}
        selectedEmployees={selectedEmployeesForRow}
        onClose={() => {
          setEmployeePickerOpen(false);
          setEmployeePickerIndex(null);
        }}
        onConfirm={(employees) => {
          if (employeePickerIndex == null) return;
          const ids = employees.map((employee) => employee.id);
          updateTemplate(employeePickerIndex, { target_user_ids: ids });
          setSelectedEmployeesByRow((prev) => {
            const next = { ...prev };
            employees.forEach((employee) => {
              next[employee.id] = employee;
            });
            return next;
          });
        }}
      />

      <SaveBar
        hasChanges={hasChanges}
        isSaving={saving}
        isSuccess={saveSuccess}
        isError={saveError}
        successMessage={t("todo.settingsSaved")}
        errorMessage={t("todo.settingsSaveError")}
        onSave={handleSaveFromBar}
        saveLabel={
          editingIndex != null ? t("todo.confirmClose.saveAndClose") : t("todo.settingsSave")
        }
      />

      <UnsavedChangesDialog
        open={confirmEditClose}
        title={t("todo.confirmClose.title")}
        message={t("todo.confirmClose.text")}
        keepEditingText={t("todo.confirmClose.keepEditing")}
        closeWithoutSavingText={t("todo.confirmClose.closeWithoutSaving")}
        saveText={t("todo.confirmClose.saveAndClose")}
        isSaving={saving}
        onKeepEditing={() => setConfirmEditClose(false)}
        onCloseWithoutSaving={closeEditDialog}
        onSave={handleSaveAndCloseEdit}
      />
    </div>
  );
};

export default TodoSettings;
